import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FastCeckinDurationFormType } from './fast-checkin-duration-form';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DateTime } from 'luxon';
import { debounceTime, distinctUntilChanged, interval, Observable, of, tap } from 'rxjs';
import { ParkingQuotaInfoTextParams } from '../../parking/helpers/parking-quota-info-text-params';
import { CalculateParkingPriceResultDTO, ForeignParkingSubscriptionInfoDTO, ParkerRegistrationOffense, ParkingProcessTariffInfoDTO, ParkingProcessZoneInfoDTO, ParkingQuotaDuration, ParkingQuotaInfoDTO } from 'src/app/base/models/models.swagger.generated';
import { FastCheckinService } from '../fast-checkin.service';
import { StringHelper } from 'src/app/base/shared/string-helper';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PriceUtils } from 'src/app/base/utils/price-utils';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { FastCheckinTimeSettingsComponent } from '../shared/fast-checkin-time-settings/fast-checkin-time-settings.component';
import { MaxParkingDurationHelper } from '../../parking/helpers/max-parking-duration.helper';
import { ParkingTimesHelper } from '../../parking/helpers/parking-times.helper';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface FastCheckinDurationSubmitData {
  durationMinutes: number;
  selectedSubscriptionId: number;
}

@Component({
  selector: 'app-fast-checkin-step-duration',
  templateUrl: './fast-checkin-step-duration.component.html',
  styleUrls: ['./fast-checkin-step-duration.component.scss'],
  imports: [TranslateModule, ReactiveFormsModule, DropDownListModule, FastCheckinTimeSettingsComponent]
})
export class FastCheckinStepDurationComponent implements OnInit {
  @Input() public zoneInfo: ParkingProcessZoneInfoDTO | null = null;

  @Input() public controlPlate: string;

  @Output() public submit = new EventEmitter<FastCheckinDurationSubmitData>();
  @Output() public back = new EventEmitter<void>();

  public form: FormGroup<FastCeckinDurationFormType>;
  public parkingPriceResult: CalculateParkingPriceResultDTO | null = null;
  public startTime = new Date();
  public customFormat = 'HH:mm';
  public availableSubscriptions: ForeignParkingSubscriptionInfoDTO[] = [];
  public readonly subscriptionDropdownFields = { text: 'subscription.name', value: 'subscription.id' };

  public get priceWithCurrency(): string {
    if (this.parkingPriceResult?.parkingPriceDetails) {
      const normalPrice = this.parkingPriceResult.parkingPriceDetails.totalPriceWithVAT;
      const fallbackPrice = this.parkingPriceResult.fallbackParkingPriceDetails?.totalPriceWithVAT ?? 0;
      const parkingPrice = Math.max(normalPrice, fallbackPrice);
      return PriceUtils.getPriceWithCurrency(parkingPrice, this.parkingPriceResult.parkingPriceDetails.currency);
    }
    return '';
  }

  public get hasFallbackPrice(): boolean {
    if (!this.parkingPriceResult?.parkingPriceDetails) {
      return false;
    }
    const normalPrice = this.parkingPriceResult.parkingPriceDetails.totalPriceWithVAT;
    if (this.parkingPriceResult?.fallbackParkingPriceDetails && this.parkingPriceResult.fallbackParkingPriceDetails?.totalPriceWithVAT > normalPrice) {
      return true;
    }
    return false;
  }

  public get normalPriceWithCurrency(): string {
    if (this.parkingPriceResult?.parkingPriceDetails) {
      const normalPrice = this.parkingPriceResult.parkingPriceDetails.totalPriceWithVAT;
      return PriceUtils.getPriceWithCurrency(normalPrice, this.parkingPriceResult.parkingPriceDetails.currency);
    }
    return '';
  }

  public get hasOffense(): boolean {
    return this.parkingPriceResult?.offense != null && this.parkingPriceResult?.offense !== ParkerRegistrationOffense.NoOffense;
  }

  public get offenseType(): string {
    return ParkerRegistrationOffense[this.parkingPriceResult?.offense];
  }

  public possibleStartTimeRanges: any[] = [];

  // check which ones are needed
  private isParkingPossible: boolean;
  private maxParkingDuration: number;
  private isParkingUnlimited: boolean;
  private maxEndTime: DateTime | null;
  private hasTariffs: boolean;
  public parkingQuotaInfoTextParams: ParkingQuotaInfoTextParams;
  private parkingQuotaInfo: ParkingQuotaInfoDTO;
  private readonly destroyRef = inject(DestroyRef);

  constructor(private fb: FormBuilder, private fastCheckinService: FastCheckinService, private translate: TranslateService) { }

  ngOnInit() {
    this.form = this.fb.group({
      parkingDuration: this.fb.control(60, Validators.required), // Default: 1 hour (60 minutes)
      selectedSubscriptionId: this.fb.control(0, [Validators.required, Validators.min(1)]),
    });

    this.refreshAvailableSubscriptionsAndSelection();
    this.loadParkingQuota().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
    this.form.controls.parkingDuration.valueChanges.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((durationMinutes) => {
      this.calculatePrice(durationMinutes);
    });
    this.form.controls.selectedSubscriptionId.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.loadParkingQuota().pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        const durationMinutes = this.form.controls.parkingDuration.value;
        if (durationMinutes) {
          const effectiveDurationMinutes = this.capDurationToMaxParkingDuration(durationMinutes);
          this.calculatePrice(effectiveDurationMinutes);
        }
      });
    });
    this.startSynchronizedTimer();
  }

  private startSynchronizedTimer() {
    // Calculate milliseconds until next minute
    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();
    if (msUntilNextMinute > 300) {
      // do not wait too long for the first update
      this.updateStartTimeAndRecalculate();
    }

    // Wait until the next minute, then start the recurring timer
    setTimeout(() => {
      // Trigger immediately when minute rolls over
      this.updateStartTimeAndRecalculate();

      // Then set up recurring timer for every minute
      interval(60000).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.updateStartTimeAndRecalculate();
      });
    }, msUntilNextMinute);
  }

  private updateStartTimeAndRecalculate() {
    // Update start time to current time
    this.startTime = new Date();

    this.refreshAvailableSubscriptionsAndSelection();

    // Recalculate the parking price with the current duration
    const currentDuration = this.form.controls.parkingDuration.value;
    if (currentDuration) {
      this.calculatePrice(currentDuration);
    }
  }

  onSubmit() {
    if (this.form.valid && !this.hasOffense) {
      this.submit.emit({
        durationMinutes: this.form.controls.parkingDuration.value,
        selectedSubscriptionId: this.form.controls.selectedSubscriptionId.value
      });
    }
  }

  getMaxSliderRange(): number {
    return this.maxParkingDuration > 0 ? this.maxParkingDuration : 1440;
  }

  public onParkingDurationChanged(durationMinutes: number) {
    this.form.controls.parkingDuration.setValue(durationMinutes);
  }

  private calculatePrice(durationMinutes: number) {
    const selectedSubscriptionId = this.form.controls.selectedSubscriptionId.value;
    if (!selectedSubscriptionId || selectedSubscriptionId <= 0) {
      this.parkingPriceResult = null;
      return;
    }

    this.fastCheckinService.calculateForeignParkingPrice(
        this.zoneInfo.id,
        selectedSubscriptionId,
        this.controlPlate,
        durationMinutes
      ).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(priceResult => {
        this.parkingPriceResult = priceResult;
      });
  }

  public getMaxParkingDurationString(): string {
    if (this.isParkingUnlimited) {
      return this.translate.instant('i18n.domain.fast-checkin.unlimited');
    }
    if (this.maxParkingDuration == null) {
      return '';
    }
    return StringHelper.transformToHoursMinutesRepresentation(this.maxParkingDuration);
  }

  public getMaxEndTimeString(): string {
    if (this.maxEndTime) {
      return this.maxEndTime.toLocaleString(DateTime.TIME_24_SIMPLE);
    }
    return this.translate.instant('i18n.domain.fast-checkin.unlimited');
  }

  private loadParkingQuota(): Observable<ParkingQuotaInfoDTO | null> {
    const zone = this.zoneInfo;
    const selectedSubscriptionId = this.form.controls.selectedSubscriptionId.value;
    if (!selectedSubscriptionId || selectedSubscriptionId <= 0) {
      this.parkingQuotaInfo = null;
      this.parkingQuotaInfoTextParams = null;
      this.refreshParkingTimesFromForm();
      this.parkingPriceResult = null;
      return of(null);
    }
    return this.fastCheckinService.getForeignParkingQuotaInfo(zone.id, selectedSubscriptionId, this.controlPlate).pipe(tap((quotaInfo) => {
      this.parkingQuotaInfo = quotaInfo;
      this.parkingQuotaInfoTextParams = null;
      if (this.parkingQuotaInfo?.remainingParkingQuotaInMinutes != null) {
        this.parkingQuotaInfoTextParams = {
          remainingQuotaInHours:  StringHelper.transformToHoursMinutesRepresentation(this.parkingQuotaInfo.remainingParkingQuotaInMinutes > 0 ? this.parkingQuotaInfo.remainingParkingQuotaInMinutes : 0),
          quotaInHours: this.parkingQuotaInfo.totalParkingQuotaInHours,
          duration: this.translate.instant('i18n.shared.parkingQuotaDuration.' + ParkingQuotaDuration[this.parkingQuotaInfo.parkingQuotaDuration]),
        }
      }

      if (this.parkingQuotaInfo?.remainingParkingQuotaInMinutes != null && this.parkingQuotaInfo?.remainingParkingQuotaInMinutes < 0) {
        this.parkingQuotaInfo.remainingParkingQuotaInMinutes = 0;
      }
      this.refreshParkingTimesFromForm();
    }));
  }

  private refreshParkingTimesFromForm() {

    const selectedSubscriptionId = this.form.controls.selectedSubscriptionId.value;
    const subscription = this.availableSubscriptions
      ?.find(info => info.subscription?.id === selectedSubscriptionId)
      ?.subscription;
    const tariffs = subscription?.tariffs;
    const maxParkingDurationInMinutes = MaxParkingDurationHelper.resolveEffectiveMaxParkingDurationInMinutes(
      subscription?.maxParkingDurationInMin,
      this.parkingQuotaInfo?.remainingParkingQuotaInMinutes
    );

    this.updateParkingTimes(tariffs, null, maxParkingDurationInMinutes, null);

    const durationMinutes = this.form.controls.parkingDuration.value;
    if (durationMinutes) {
      this.capDurationToMaxParkingDuration(durationMinutes);
    }
  }

  private capDurationToMaxParkingDuration(durationMinutes: number): number {
    if (this.maxParkingDuration == null || this.maxParkingDuration <= 0) {
      return durationMinutes;
    }

    const cappedDuration = Math.min(durationMinutes, this.maxParkingDuration);
    if (cappedDuration !== durationMinutes) {
      this.form.controls.parkingDuration.setValue(cappedDuration, { emitEvent: false });
    }

    return cappedDuration;
  }

  private refreshAvailableSubscriptionsAndSelection() {
    const now = DateTime.local();
    this.availableSubscriptions = (this.zoneInfo?.foreignParkingSubscriptionInfos ?? []).sort((a, b) => (a.subscription?.name ?? '').localeCompare(b.subscription?.name ?? ''));

    this.syncSelectedSubscriptionControlState();

    const selectedSubscriptionId = this.form.controls.selectedSubscriptionId.value;
    const hasCurrentSelection = this.availableSubscriptions.some((subscriptionInfo) => subscriptionInfo.subscription?.id === selectedSubscriptionId);
    if (hasCurrentSelection) {
      return;
    }

    const fastCheckinSubscriptionId = this.zoneInfo?.fastCheckinSubscriptionId;
    const defaultFastCheckinSubscription = this.availableSubscriptions
      .find((subscriptionInfo) => subscriptionInfo.subscription?.id === fastCheckinSubscriptionId)
      ?.subscription?.id;
    const fallbackSubscription = this.availableSubscriptions.at(0)?.subscription?.id;
    const nextSelection = defaultFastCheckinSubscription ?? fallbackSubscription ?? 0;

    this.form.controls.selectedSubscriptionId.setValue(nextSelection, { emitEvent: false });
  }

  private syncSelectedSubscriptionControlState() {
    const selectedSubscriptionControl = this.form.controls.selectedSubscriptionId;
    const shouldBeEnabled = this.availableSubscriptions.length > 1;

    if (shouldBeEnabled) {
      selectedSubscriptionControl.enable({ emitEvent: false });
      return;
    }

    selectedSubscriptionControl.disable({ emitEvent: false });
  }

  private updateParkingTimes(tariffs: ParkingProcessTariffInfoDTO[], tariffsMaxValidityTime: DateTime,
                                subscriptionsMaxParkingDuration: number, fallbackParkingTariffs: ParkingProcessTariffInfoDTO[], ): void {
    const parkingTimesState = ParkingTimesHelper.calculateParkingTimesState(
      DateTime.utc(),
      tariffs,
      tariffsMaxValidityTime,
      subscriptionsMaxParkingDuration,
      fallbackParkingTariffs,
      {
        maxEndTime: this.maxEndTime,
        hasTariffs: this.hasTariffs,
      }
    );

    this.isParkingPossible = parkingTimesState.isParkingPossible;
    this.maxParkingDuration = parkingTimesState.maxParkingDuration;
    this.isParkingUnlimited = parkingTimesState.isParkingUnlimited;
    this.maxEndTime = parkingTimesState.maxEndTime;
    this.hasTariffs = parkingTimesState.hasTariffs;
  }

}
